import { type QueryClient, useQuery } from "@tanstack/react-query"
import type { RealtimeMessage } from "@zplanem/responses"
import { queryKeys } from "../../lib/query-keys"

/**
 * "Mareczek is working" indicator state for one chat thread, held in the
 * React Query cache so the channel hooks (writers) and the chat panels
 * (readers) share one source of truth without prop-drilling. Keyed by chat
 * thread (ZPL-152): the private chat, a shared property chat and a project
 * chat on the same property each get an independent spinner.
 *
 * Set true on send (covers a direct reply with no tool call) and on a
 * `mareczek.thinking` event; cleared on `mareczek.thinking-cleared`, when a
 * non-user message lands in the thread, or by a timeout fallback.
 */
export interface MareczekThinkingState {
	active: boolean
	/**
	 * The model's latest per-turn narration ("Sprawdzam bilety…") from the
	 * `mareczek.thinking` payload (ZPL-294) — the server always sent it; the
	 * web used to reduce it to a boolean and render only bouncing dots.
	 * Null before the first thinking event of a turn (e.g. right after send).
	 */
	text: string | null
}

const IDLE: MareczekThinkingState = { active: false, text: null }

export function useMareczekThinking(
	chatId: string | undefined,
): MareczekThinkingState {
	const { data } = useQuery({
		queryKey: chatId
			? queryKeys.mareczekThinking(chatId)
			: ["mareczek-thinking", "disabled"],
		// Pure client state: never fetched, only written via setMareczekThinking.
		queryFn: () => IDLE,
		enabled: false,
		initialData: IDLE,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
	})
	return data ?? IDLE
}

export function setMareczekThinking(
	queryClient: QueryClient,
	chatId: string,
	active: boolean,
	text: string | null = null,
): void {
	queryClient.setQueryData(queryKeys.mareczekThinking(chatId), {
		active,
		text: active ? text : null,
	} satisfies MareczekThinkingState)
}

/**
 * Routes a realtime Mareczek event into the per-thread indicator cache.
 * Shared by the private, shared-property and project channel hooks. Events
 * without a threadId (an older API during a rolling deploy) are ignored
 * rather than misattributed.
 */
export function applyMareczekEventToThinkingCache(
	queryClient: QueryClient,
	msg: RealtimeMessage,
): void {
	if (
		msg.event !== "mareczek.thinking" &&
		msg.event !== "mareczek.thinking-cleared"
	) {
		return
	}
	const threadId = msg.payload?.threadId
	if (!threadId) return
	if (msg.event === "mareczek.thinking") {
		setMareczekThinking(queryClient, threadId, true, msg.payload.text ?? null)
	} else {
		setMareczekThinking(queryClient, threadId, false)
	}
}
